import React from "react";
import axios from "axios";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Copy, Check, Image as ImageIcon, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { copyToClipboard as copyText } from "../../utils/clipboard";
import RegenerateSection from "./RegenerateSection";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function LinkedInTab({ content, activeSlide, setActiveSlide, onRegenerate, isRegenerating, onRegenerateItem, regeneratingItem }) {
  const [copied, setCopied] = React.useState(false);
  const [slideImages, setSlideImages] = React.useState({});
  const [generatingImage, setGeneratingImage] = React.useState(null);

  const copyToClipboard = async (text) => {
    const success = await copyText(text);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const generateImage = async (slide) => {
    setGeneratingImage(slide.slideNum);
    try {
      const prompt = `Create a professional LinkedIn carousel image for: ${slide.title}. ${slide.content.substring(0, 200)}`;
      
      const response = await axios.post(`${API}/images/generate`, {
        prompt,
        content_type: "linkedin"
      });

      setSlideImages(prev => ({
        ...prev,
        [slide.slideNum]: response.data.image_url
      }));

      toast.success("Image generated!");
    } catch (error) {
      console.error("Image generation error:", error);
      toast.error("Failed to generate image");
    } finally {
      setGeneratingImage(null);
    }
  };

  const copyAll = () => {
    const allText = content.map(slide => 
      `Slide ${slide.slideNum}\n${slide.title}\n\n${slide.content}`
    ).join("\n\n---\n\n");
    copyToClipboard(allText);
  };

  if (!content || content.length === 0) {
    return (
      <div className="text-center text-white/60 py-12">
        <p className="mb-4">No LinkedIn content generated yet.</p>
        {onRegenerate && <RegenerateSection onRegenerate={onRegenerate} isRegenerating={isRegenerating} contentType="LinkedIn" />}
      </div>
    );
  }

  const currentSlide = content[activeSlide];

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fadeIn">
      {/* Preview Card */}
      <Card className="card overflow-hidden">
        <div className="bg-gradient-to-br from-[#0077b5] to-[#005885] p-6">
          <div className="bg-white rounded-lg p-8 min-h-[400px] flex flex-col">
            <div className="font-mono text-xs text-gray-500 mb-4">
              SLIDE {currentSlide.slideNum} OF {content.length}
            </div>
            
            {/* Generated Image */}
            {slideImages[currentSlide.slideNum] && (
              <div className="mb-4 rounded-lg overflow-hidden">
                <img 
                  src={slideImages[currentSlide.slideNum]} 
                  alt={currentSlide.title}
                  className="w-full h-48 object-cover"
                />
              </div>
            )}
            
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
              {currentSlide.title}
            </h3>
            <div className="flex-1 text-lg text-gray-800 whitespace-pre-wrap">
              {currentSlide.content}
            </div>
            <div className="mt-8 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="font-mono text-sm font-semibold text-[#0077b5]">
                  EPSILLON MEDIA
                </div>
                <div className="text-sm text-gray-500">
                  epsillonmedia.com
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Slide Navigation */}
      <div className="flex items-center justify-between">
        <Button
          onClick={() => setActiveSlide(Math.max(0, activeSlide - 1))}
          disabled={activeSlide === 0}
          variant="outline"
        >
          ← Previous
        </Button>

        <div className="flex gap-2">
          {content.map((_, index) => (
            <div key={index} className="relative group">
              <button
                onClick={() => setActiveSlide(index)}
                className={`w-8 h-8 rounded-full font-semibold transition-all ${
                  index === activeSlide
                    ? "bg-[#00d4aa] text-black"
                    : "bg-white/10 text-white/60 hover:bg-white/20"
                }`}
              >
                {regeneratingItem === index ? (
                  <RefreshCw size={12} className="animate-spin mx-auto" />
                ) : (
                  index + 1
                )}
              </button>
              {onRegenerateItem && (
                <button
                  onClick={() => onRegenerateItem(index)}
                  disabled={regeneratingItem === index}
                  className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-white/20 hover:bg-white/40 text-white/60 hover:text-white hidden group-hover:flex items-center justify-center transition-all"
                  title={`Regenerate slide ${index + 1}`}
                >
                  <RefreshCw size={8} />
                </button>
              )}
            </div>
          ))}
        </div>

        <Button
          onClick={() => setActiveSlide(Math.min(content.length - 1, activeSlide + 1))}
          disabled={activeSlide === content.length - 1}
          variant="outline"
        >
          Next →
        </Button>
      </div>

      {/* Action Buttons */}
      <div className="space-y-3">
        <Button
          onClick={() => generateImage(currentSlide)}
          disabled={generatingImage === currentSlide.slideNum}
          variant="outline"
          className="w-full border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
        >
          {generatingImage === currentSlide.slideNum ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Generating Image...
            </>
          ) : (
            <>
              <ImageIcon className="w-4 h-4 mr-2" />
              Generate Image with Gemini
            </>
          )}
        </Button>

        <div className="flex gap-4">
          <Button
            onClick={() => copyToClipboard(currentSlide.title + "\n\n" + currentSlide.content)}
            className="flex-1 bg-[#00d4aa] hover:bg-[#00e6bb] text-black font-semibold"
          >
            {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
            Copy Current Slide
          </Button>
          <Button
            onClick={copyAll}
            variant="outline"
            className="flex-1"
          >
            Copy All Slides
          </Button>
        </div>
      </div>

      {/* Regenerate Section */}
      {onRegenerate && (
        <RegenerateSection
          onRegenerate={onRegenerate}
          isRegenerating={isRegenerating}
          contentType="LinkedIn"
        />
      )}
    </div>
  );
}

export default LinkedInTab;